import { useScopedI18n } from '@/locales/client'
import TameeniLoaderOverlay from '../tameeni-loader/tameeni-loader-overlay'
import { Button } from '@tameeni/ui'
import { useLogin } from '@/features/authentication'
import { useLocation, useSearchParams } from 'react-router-dom'
import { useNavigate } from '@/hooks/use-navigate'
import { useAuth } from 'react-oidc-context'
import { useRef } from 'react'

export const protectedRoutes = [
  'dashboard',
  'professional-info',
  'declarations',
  'quote-list',
  'summary-and-payment',
  'payment-result',
  'policy-renewal',
]

export default function ProtectedRoutesErrorContainer({
  children,
}: {
  readonly children: React.ReactNode
}) {
  const tError = useScopedI18n('common.error')
  const { isAuthenticated, isLoading } = useAuth()
  const { signIn } = useLogin()
  const location = useLocation()
  const navigate = useNavigate()
  const pathname = location.pathname
  const [searchParams] = useSearchParams()
  const isProtected = !!protectedRoutes.find((route) =>
    pathname.includes(route)
  )

  // Block render only on the FIRST OIDC initialisation.
  // After that, silent renew / popup cycles flip isLoading=true again —
  // keep the tree mounted so React Query state survives.
  const initialLoadDoneRef = useRef(false)
  if (!isLoading && !initialLoadDoneRef.current) {
    initialLoadDoneRef.current = true
  }
  const isFirstAuthInit = isLoading && !initialLoadDoneRef.current

  if (isFirstAuthInit) {
    return <TameeniLoaderOverlay transparent={false} />
  }

  if (isProtected && !isAuthenticated && !isLoading) {
    return (
      <div className='flex h-full flex-col items-center justify-center gap-2'>
        <h1 className='text-h1'>{tError('unauthenticated')}</h1>
        <p className='text-s1 text-slate-500'>
          {tError('unauthenticated_desc')}
        </p>
        <div className='mt-6 flex gap-2'>
          <Button
            onClick={() => {
              const currentUrl =
                pathname +
                (searchParams.toString() ? `?${searchParams.toString()}` : '')
              const params = new URLSearchParams(searchParams?.toString() ?? '')
              params.set('postLoginRedirectionUrl', currentUrl)
              navigate(`${pathname}?${params.toString()}`, { replace: true })
              signIn({ postLoginRedirectionUrl: currentUrl })
            }}
          >
            {tError('login')}
          </Button>
          <Button variant={'outline'} onClick={() => navigate('/')}>
            {tError('go_to_home_page')}
          </Button>
        </div>
      </div>
    )
  }
  return <>{children}</>
}
